/**
 * Formula evaluation — `ModelLispEnvironment` + `ModelLispLibrary`.
 *
 * Provides the Lisp evaluation environment for `FormulaDataSource`
 * cells. Row references resolve through the sheet's label /
 * lookup-key / id indices; cell reads pull through
 * `SourceRow.peekCells` so local and canonical stores share one path.
 * Builtin libraries layer as:
 *
 *   ControlFlow       — generic runtime (if/var/let/…)
 *   StandardLibrary   — generic math + logic
 *   ModelLispLibrary  — date / style / rendering helpers tied
 *                       to `ModelFormulaContext` (this module)
 *
 * Port of `tento/tento-lona-js/src/formula/formula.ts` — the library is the
 * authoritative copy for every consumer. lona-js will
 * re-export from here as it collapses into tento/tento-lona-js/sheets.
 */

import { AuthId, CalendarId, EventId, SlotId, TaskId, UserId } from "../ids/ids.ts";
import { FormulaEngine } from "./formula-engine.ts";
import type { Option } from "../core/option.ts";
import {
  DateTime,
  MsSinceEpoch,
  NaiveDate,
  TimezoneRegion,
  type Utc,
  Year,
  YearMonth,
} from "@tento-chrono";
import { Lisp, flattenLib } from "@tento-lisp/lisp";
import { ControlFlow } from "@tento-lisp/ops/control-flow";
import { StandardLibrary } from "@tento-lisp/ops/stdlib";
import { nonnull } from "@tento-core/assert";

import type {
  LogEntry,
  Report,
  Slot,
  Sticker,
  TzClock,
  Weather,
} from "../core/dtype.ts";

import { CalendarEvent, type CellStyle, type CellValue, TaskCellValue } from "../core/dtype.ts";
import type { Json } from "../core/json.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import type { ModelRow } from "../rows/model-row.ts";
import {
  calendarSystemCellData,
  normalizeCalendarSystemName,
} from "../rows/variants/calendar-system-row.ts";
import { peekDateCells, SourceRow } from "../rows/variants/source-row.ts";
import { Internals } from "../core/internals.ts";

/**
 * Context threaded into every `ModelLispEnvironment` evaluation.
 * Carries the cell location (`sheet` + `row` + `column`) so row
 * references resolve to the right cell, plus the mutable `style`
 * bag that `cell.bg` / other style helpers write into, an
 * optional `tz` that date helpers read (falling back to UTC),
 * and an opaque `dbg` hook that `render.*` helpers consult.
 */
export interface ModelFormulaContext {
  readonly sheet: ModelSheet;
  readonly row: ModelRow;
  readonly column: NaiveDate.Partial;
  /** Style side-effect bag — writes land here via `cell.bg` etc. */
  readonly style: CellStyle;
  /** Timezone for date-library helpers. `null` defaults to UTC. */
  readonly tz: Option<TimezoneRegion>;
  /** Debug hook consulted by `render.offset`: the render loop's offset. */
  readonly dbg: Option<{ readonly offset: Option<number> }>;
}

/** What a symbol names as a row reference: `rows["Label"]`, `r_<32 hex>`, or neither (`null`). */
type RowRefKey =
  | { readonly kind: "label"; readonly nameOrKey: string }
  | { readonly kind: "id"; readonly rawId: string };

const ROWS_PREFIX = 'rows["';
const ROWS_SUFFIX = '"]';

/** Whether `code` is a hex digit (either case). */
function isHexCode(code: number): boolean {
  return (code >= 48 && code <= 57) || (code >= 97 && code <= 102) || (code >= 65 && code <= 70);
}

/**
 * What `key` names as a row reference — `/^rows\["([^"]+)"\]$/` (a label
 * or lookup key) or `/^r_([0-9a-f]{32})$/i` (a row id) — read without a
 * regular expression: `get` runs for every symbol of every
 * evaluation (row references, builtins, `+`), and matching both patterns per
 * lookup was the formula engine's hottest line on a dense sheet. A builtin
 * is told apart by its first characters; which row a reference names is
 * resolved per lookup (a rename or a rebind needs nothing here).
 */
function rowRefKey(key: string): Option<RowRefKey> {
  if (key.startsWith(ROWS_PREFIX)) {
    if (key.length <= ROWS_PREFIX.length + ROWS_SUFFIX.length || !key.endsWith(ROWS_SUFFIX)) return null;
    const nameOrKey = key.slice(ROWS_PREFIX.length, key.length - ROWS_SUFFIX.length);
    return nameOrKey.includes('"') ? null : { kind: "label", nameOrKey };
  }
  const r = key.charCodeAt(0);
  if (key.length !== 34 || (r !== 114 && r !== 82) /* r, R */ || key.charCodeAt(1) !== 95 /* _ */) return null;
  for (let i = 2; i < 34; i++) if (!isHexCode(key.charCodeAt(i))) return null;
  return { kind: "id", rawId: `r_${key.slice(2)}` };
}

/**
 * A formula that tests for absence itself: it calls `null?` or `empty?`
 * (the formula Lisp's only absence tests — it has no coalesce or default
 * form, and `or` answers a boolean). Such a formula keeps the value it
 * computes on a day an input has no value.
 */
const TESTS_ABSENCE_REGEX = /\((?:null\?|empty\?)[\s)]/;

/**
 * Whether `formula` tests for absence itself (`null?` / `empty?`), so it
 * keeps the value it computes over a missing input. The one test both
 * formula evaluators apply (this module's `evaluateFormula` and
 * `@tento/lona`'s `Formula.run`).
 */
export function formulaTestsAbsence(formula: string): boolean {
  return TESTS_ABSENCE_REGEX.test(formula);
}

/** `ModelLispEnvironment`'s friend record (`Internals`): its constructor registers each instance. */
type ModelLispEnvironmentInternals = {
  readonly readMissingInput: (env: ModelLispEnvironment) => boolean;
};
const environmentInternals = new Internals<ModelLispEnvironment, ModelLispEnvironmentInternals>("ModelLispEnvironment");

/**
 * The Lisp environment a formula evaluates in on the rows side, over a
 * sheet's rows and cells.
 */
export class ModelLispEnvironment implements Lisp.Environment {
  readonly #context: ModelFormulaContext;
  readonly #scope: Map<string, Option<Lisp.Expression>>;
  /**
   * Shared by an environment and every clone of it (one evaluation): a row
   * reference found no value on the evaluated day (`evaluateFormula`).
   */
  #reads: { missing: boolean } = { missing: false };

  static readonly #internals: ModelLispEnvironmentInternals = {
    readMissingInput: (env) => env.#reads.missing,
  };

  constructor(
    scope: Map<string, Option<Lisp.Expression>>,
    context: ModelFormulaContext,
    public readonly evalCount: Lisp.EvalCount = { n: 0 },
  ) {
    environmentInternals.register(this, ModelLispEnvironment.#internals);
    this.#scope = scope;
    this.#context = context;
  }

  clone(keys: string[], values: Lisp.Expression[]): Lisp.Environment {
    const cloned = new Map(this.#scope);
    keys.forEach((key, index) => {
      const value = values[index];
      if (value !== undefined) cloned.set(key, value);
    });
    const env = new ModelLispEnvironment(cloned, this.#context, this.evalCount);
    env.#reads = this.#reads;
    return env;
  }

  set(key: string, value: Lisp.Expression): void {
    this.#scope.set(key, value);
  }

  get(key: string): Option<Lisp.Expression> {
    if (this.#scope.has(key)) {
      return this.#scope.get(key) ?? null;
    }

    const ref = rowRefKey(key);
    // rows["Label"] or rows["lookup:key"]
    if (ref?.kind === "label") {
      const nameOrKey = ref.nameOrKey;
      const target =
        FormulaEngine.resolveReference(this.#context.sheet, nameOrKey);
      if (!target) {
        throw new Error(`Row not found: "${nameOrKey}"`);
      }
      return this.#input(rowValueAtColumn(target, this.#context.column));
    }

    // r_<32 hex>
    if (ref?.kind === "id") {
      const sheet = this.#context.sheet;
      const target = sheet.peekRow(sheet.registry.resolveFromRawId(ref.rawId));
      if (!target) {
        throw new Error(`Row not found with id: "${key}"`);
      }
      return this.#input(rowValueAtColumn(target, this.#context.column));
    }

    if (ModelLispLibrary.has(key)) {
      const contextFn = nonnull(ModelLispLibrary.get(key), "formula: library fn");
      return contextFn(this.#context);
    }
    if (ControlFlow.has(key)) {
      return ControlFlow.get(key) ?? null;
    }
    if (StandardLibrary.has(key)) {
      return StandardLibrary.get(key) ?? null;
    }
    // Fall through: treat unknown symbols as string literals — matches
    // `Lisp.Environment.Default` semantics when a symbol is neither
    // bound nor a builtin.
    return key;
  }

  /** A row reference's value; `null` (the row has no value that day) marks the evaluation. */
  #input(value: Option<Lisp.Atom>): Option<Lisp.Atom> {
    if (value === null) this.#reads.missing = true;
    return value;
  }
}

/**
 * Read the scalar cell value for `row` at `column`. Returns `null` (no
 * value) when the row has no cell-bearing source, no cell at the date, or
 * a cell holding no value; the environment marks the evaluation, and
 * `evaluateFormula` then answers no value for the day (a missing input is
 * not zero).
 *
 * Cross-scale fallback: when no exact-date match exists, walk the
 * row's cells for one whose range contains `column.start`. Lets
 * daily formulas read week/month-scale sources without explicit
 * time-scale coercion — Case 6 workaround per
 * `formula-cardinality-cases.md` §4.3 (viewport soundness preserved
 * because reads don't write to other dates).
 */
function rowValueAtColumn(
  row: ModelRow,
  column: NaiveDate.Partial,
): Option<Lisp.Atom> {
  const source = cellBearingRow(row);
  if (source == null) return null;
  // An exact match starts on `column.start`: read that one day first. The
  // stored entries come back in the full read's order, so its first exact
  // match is the one a scan of every cell finds — and the common case (a
  // value on the day) never reads the year of cells the fallback needs.
  const sameDay = peekDateCells(source, new NaiveDate.Range(column.start, column.start.addDays(1)));
  for (const entry of sameDay) {
    if (entry.date.type === column.type && entry.date.index === column.index) {
      return coerceCellValue(entry.value);
    }
  }
  // No exact match: one bounded read of the date lane, not the whole row —
  // a containing cell of another scale starts at most `LONGEST_PERIOD_DAYS`
  // before `column.start`.
  const cells = peekDateCells(
    source,
    new NaiveDate.Range(column.start.addDays(-LONGEST_PERIOD_DAYS), column.start.addDays(1)),
  );
  for (const entry of cells) {
    if (entry.date.type === column.type) continue;
    if (entry.date.toRange().contains(column.start)) {
      return coerceCellValue(entry.value);
    }
  }
  return null;
}

/**
 * The longest period a date-keyed cell spans (a leap year): no cell that
 * starts earlier than this before a date can contain it.
 */
const LONGEST_PERIOD_DAYS = 366;

function coerceCellValue(value: Option<Json | CellValue>): Option<Lisp.Atom> {
  if (value == null) return null;
  if (typeof value === "string") {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? normalizeFormulaAtom(n) : value;
  }
  return normalizeFormulaAtom(atomOfCell(value));
}

/**
 * A cell's value as the formula runtime holds it. Every cell value is a
 * `Lisp.Atom` (a string, number, boolean or object) except an empty cell's
 * `null`, which the runtime reads as nil and tento-lisp's `Lisp.Atom` does not
 * spell: the one place a cell value crosses into the runtime as is.
 */
function atomOfCell(value: Option<Json | CellValue>): Lisp.Atom {
  return value as Lisp.Atom;
}

export function cellBearingRow(row: ModelRow): Option<SourceRow> {
  if (row instanceof SourceRow) return row;
  for (const child of row.children) {
    const source = cellBearingRow(child);
    if (source !== null) return source;
  }
  return null;
}

function normalizeFormulaAtom(value: Lisp.Atom): Lisp.Atom {
  return typeof value === "number" && Object.is(value, -0) ? 0 : value;
}

/**
 * Parse + evaluate a formula string against a context. Throws on
 * parse errors or reference resolution failures. Returns the
 * evaluated value, or `null` if the expression evaluates to null /
 * undefined / a bare procedure (no value to write).
 *
 * A missing input is no value: when a row reference (`rows["X"]`, by
 * label or lookup key, or `r_<id>`) finds no value on the evaluated day,
 * the formula has no value that day — `null`, whatever the rest of the
 * expression would compute, and also when the arithmetic then throws on
 * the `nil` — unless the formula tests for absence itself (`null?` /
 * `empty?`, `TESTS_ABSENCE_REGEX`), which keeps what it computes.
 * `(cells "X")` reads a collection: an empty day is a value (`(count …)`
 * is `0`). A formula with no reference has a value on every day.
 *
 * Accepts every `CellValue` shape — scalars (number / boolean /
 * string), arrays (GraphRow series data), and objects (Event /
 * Weather / Slot / …). Objects flow through `sourceCellData.set`
 * unchanged; readers that care about shape probe through the
 * `ShapeRegistry` facade layer (the return type is widened on purpose).
 */
export function evaluateFormula(
  formula: string,
  context: ModelFormulaContext,
): Option<Lisp.Atom> {
  const expr = Lisp.unsafeParse(formula);
  const env = new ModelLispEnvironment(new Map(), context);
  const handlesAbsence = formulaTestsAbsence(formula);
  let result: ReturnType<typeof Lisp.evalThrows>;
  try {
    result = Lisp.evalThrows(expr, env);
  } catch (err) {
    // `(+ nil 10)` throws in the arithmetic: the day has no value, not an error.
    if (environmentInternals.of(env).readMissingInput(env) && !handlesAbsence) return null;
    throw err;
  }
  if (environmentInternals.of(env).readMissingInput(env) && !handlesAbsence) return null;
  if (result == null) return null;
  // Procedures are values in Lisp but can't live in cells. A formula
  // returning a bare lambda is either a Case 4 body that the engine
  // should have dispatched to `evaluateCase4AtDate` (single-card
  // path shouldn't see one), or a programmer error — drop to null.
  if (typeof result === "function") return null;
  return normalizeFormulaAtom(result);
}

// ── Typed-object constructor helpers ───────────────────────────────

/** `requireString`, as an id `parse` accepts (a non-empty string). */
function requireId<Id>(
  v: unknown,
  shape: string,
  field: string,
  parse: (raw: string) => Option<Id>,
): Id {
  const id = parse(requireString(v, shape, field));
  if (id == null) throw new Error(`${shape}: field "${field}" is not an id`);
  return id;
}

/**
 * The provider identity of a formula-synthesized event: it comes from no
 * calendar provider, so there is no authId/calendarId to inherit. The
 * `"formula"` sentinel keeps `Event`'s required-field contract (decoder-level
 * enforcement for wire events) without making formula callers construct a
 * bespoke provider tuple.
 */
const FORMULA_PROVIDER = {
  authId: AuthId.of("formula"),
  calendarId: CalendarId.of("formula"),
} as const;

/**
 * A text field: a string, or a number / boolean spelled as text. An object,
 * an array or a function is refused (it has no text of its own — `String`
 * would write `[object Object]`).
 */
function requireString(v: unknown, shape: string, field: string): string {
  if (v === undefined || v === null) {
    throw new Error(`${shape}: missing required field "${field}"`);
  }
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  throw new Error(`${shape}: field "${field}" must be text (got ${typeof v})`);
}

function requireNumber(v: unknown, shape: string, field: string): number {
  if (v === undefined || v === null) {
    throw new Error(`${shape}: missing required field "${field}"`);
  }
  const n = Number(v);
  if (Number.isNaN(n)) {
    throw new Error(`${shape}: field "${field}" must be a number`);
  }
  return n;
}

/**
 * A `DateTime` field: a `DateTime` instance (any zone), read as the same
 * instant in UTC. Anything else — a string, a plain object, a `NaiveDate` —
 * is refused.
 */
function requireDateTime(
  v: unknown,
  shape: string,
  field: string,
): DateTime<Utc> {
  if (v === undefined || v === null) {
    throw new Error(`${shape}: missing required field "${field}"`);
  }
  if (!(v instanceof DateTime)) {
    throw new Error(
      `${shape}: field "${field}" must be a DateTime (got ${typeof v === "object" ? v.constructor?.name ?? "object" : typeof v})`,
    );
  }
  return v.toTz(DateTime.utc);
}

/**
 * Factory for `<shape>.extend` builtins. Shallow-merges a patch
 * object over the base. Both must be plain objects. Used by
 * decorator formulas that layer computed fields onto a canonical
 * shape.
 */
function extendOf<T>(shape: string) {
  return (_context: ModelLispContext) =>
    (args: Lisp.Atom[]): Lisp.Atom => {
      const [base, patch] = args;
      if (!base || typeof base !== "object" || Array.isArray(base)) {
        throw new Error(`${shape}.extend: base must be an object`);
      }
      if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
        throw new Error(`${shape}.extend: patch must be an object`);
      }
      // `T` names the shape a call extends; the merge is shallow and unchecked.
      return { ...base, ...patch };
    };
}

// ── putStyle helper ─────────────────────────────────────────────────

/**
 * Curry a style-setter into the evaluation environment. Called
 * by style builtins like `cell.bg` to produce a Lisp function
 * that writes into `context.style`.
 *
 * Shapes:
 *   `(cell.bg "red")`         → context.style.set("bg", "red")
 *   `(cell.bg 2 "red")`       → context.style.set("bg.2", "red")
 *   `(cell.bg)` / `(cell.bg null null)` → deletes the key
 */
function putStyle(key: string, context: ModelLispContext) {
  return ([a0, a1]: Lisp.Atom[]): Lisp.Atom => {
    if (a0 == null && a1 == null) {
      context.style.delete(key);
      return 0;
    }
    if (typeof a0 === "number") {
      const k = `${key}.${Lisp.Utils.assertNum(a0, "style")}`;
      const v = Lisp.Utils.toStr(a1);
      v ? context.style.set(k, v) : context.style.delete(k);
      return 0;
    }
    const k = key;
    const v = Lisp.Utils.toStr(a0);
    v ? context.style.set(k, v) : context.style.delete(k);
    return 0;
  };
}

// ── ModelLispLibrary ────────────────────────────────────────────────

/**
 * Minimal sheet surface the cross-package `ModelLispLibrary` reads.
 * Kept deliberately narrow so consumers with richer data models (e.g.
 * lona-js's `SheetRowTree` + `CellAccessor`) can supply an adapter
 * rather than implementing the full `Sheet` class. The concrete
 * `Sheet` class from this package satisfies it structurally.
 *
 * Id types stay internal to each implementation — the view collapses
 * the "find row by label or lookup key, then iterate its cells" path
 * into one method so row-id identity doesn't cross the boundary
 * (useful for lona-js, which has its own `RowLocalId`).
 */
export interface FormulaSheetReads {
  /** The row's date-keyed cells; `null` when no row has that label or key. */
  peekCellsByLabelOrKey(labelOrKey: string): Option<Iterable<{
    readonly date: NaiveDate.Partial;
    readonly value: CellValue;
  }>>;
}

/**
 * Narrow context shape the library actually reads. Anything
 * structurally matching this is accepted — the library is
 * intentionally decoupled from the full `ModelFormulaContext` so
 * consumers with richer contexts (e.g. lona-js's `FormulaContext`
 * with `tree` + `SheetRow.Joined.Handle`) can pass their own
 * context without nominal-type friction.
 */
export interface ModelLispContext {
  readonly sheet: FormulaSheetReads;
  readonly column: NaiveDate.Partial;
  readonly style: CellStyle;
  readonly tz: Option<TimezoneRegion>;
  /** Debug hook consulted by `render.offset`: the render loop's offset. */
  readonly dbg: Option<{ readonly offset: Option<number> }>;
}

/** `(render.offset)`: the debug offset the render loop threads through `dbg`. */
function renderOffset(context: ModelLispContext): Lisp.Atom {
  return context.dbg?.offset ?? 0;
}

/**
 * Context-bound builtins that operate on the current
 * `ModelLispContext`. Each entry is a factory — `Lisp.Expression`
 * values are produced on demand via `contextFn(context)`.
 *
 * Ported verbatim from lona-js. Library categories:
 *   `render.*`   — rendering hooks (pure debug helpers today).
 *                  `_render.offset` stays resolvable as the legacy
 *                  spelling saved formulas and sheet templates carry.
 *   `cell.*`     — per-cell style mutators.
 *   `date.*`     — date / tz arithmetic on `column`.
 */
export const ModelLispLibrary: Map<string, (context: ModelLispContext) => Lisp.Atom> = flattenLib<
  (context: ModelLispContext) => Lisp.Atom
>({
  render: {
    offset: renderOffset,
  },
  /** @deprecated Use `render.offset`. Kept without a removal release: saved formulas spell it this way. */
  "_render.offset": renderOffset,
  cell: {
    bg: (context: ModelLispContext) => putStyle("bg", context),
  },
  /**
   * `(cells "LABEL")` — read every cell for a multi-card row at
   * the current column. Returns an array of cell values (possibly
   * empty). Case 3 (multi → single, reduce) reads this into a
   * reducer like `(count …)` / `(sum …)`.
   *
   * Label resolution matches `(rows["LABEL"])` — lookup-key first,
   * then label. Throws if the row is missing.
   *
   * Dependency tracking: the formula-engine scans for `cells
   * "LABEL"` and adds the referenced row as a dep — see
   * `CELLS_REF_REGEX` in `formula-engine.ts`.
   */
  cells: (context: ModelLispContext) => (args: Lisp.Atom[]): Lisp.Atom => {
    const label = Lisp.Utils.toStr(args[0] ?? null);
    if (!label) {
      throw new Error("cells: expected label or lookup key as string");
    }
    const iter = context.sheet.peekCellsByLabelOrKey(label);
    if (!iter) {
      throw new Error(`cells: row not found: "${label}"`);
    }
    const date = context.column;
    const out: Lisp.Atom[] = [];
    for (const entry of iter) {
      if (entry.date.type !== date.type) continue;
      if (entry.date.index !== date.index) continue;
      out.push(atomOfCell(entry.value));
    }
    return out;
  },
  /**
   * `(object :key value …)` — build a plain JSON object from
   * keyword-value pairs. Alias of StandardLibrary's `hash-map` so
   * formula authors can write the design-doc form literally.
   */
  object: (_context: ModelLispContext) =>
    StandardLibrary.get("hash-map"),
  /**
   * `(calendar-system :hebrew (date.column))` — compute the
   * secondary-calendar cell object for an explicit system + date.
   * The helper is shared with `CalendarSystemRow`, so builtin and
   * computed-row paths use the same bounded cache.
   */
  "calendar-system": (_context: ModelLispContext) =>
    (args: Lisp.Atom[]): Lisp.Atom => {
      const system = calendarSystemArg(args[0] ?? null);
      const date = calendarSystemDateArg(args[1] ?? null);
      const data = calendarSystemCellData(system, date);
      if (data === null) {
        throw new Error(`calendar-system: unknown system "${system}"`);
      }
      return data;
    },
  /**
   * Typed-object constructors for `dtype=obj` formula outputs.
   * Positional — the common hot-path fields are required; the long
   * tail of optional fields is layered on via `<shape>.extend` with
   * an `(object :k v …)` patch. Shapes mirror `dtype.ts`'s canonical
   * interfaces (`Event`, `TaskCellValue`, `Weather`, …).
   *
   * Typed constructors, one per shape, with positional arguments.
   */
  event: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [id, title, start, end, location] = args;
        // Formula-synthesized events get the fixed `FORMULA_PROVIDER`.
        const out = CalendarEvent.of({
          id: requireId(id, "event", "id", EventId.parse),
          title: requireString(title, "event", "title"),
          ...FORMULA_PROVIDER,
          start: requireDateTime(start, "event", "start"),
          end: requireDateTime(end, "event", "end"),
          ...(location !== undefined && location !== null ? { location: String(location) } : {}),
        });
        return out;
      },
    extend: extendOf<CalendarEvent>("event"),
  },
  task: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [id, title, status, dueDate] = args;
        let parsedStatus: Option<TaskCellValue.Status> = null;
        if (status !== undefined && status !== null) {
          parsedStatus = TaskCellValue.Status.parse(requireString(status, "task", "status"));
          if (parsedStatus == null) {
            throw new Error(
              `task: field "status" must be one of ${TaskCellValue.Status.ALL.join(", ")}`,
            );
          }
        }
        const out: TaskCellValue = TaskCellValue.of({
          id: requireId(id, "task", "id", TaskId.parse),
          title: requireString(title, "task", "title"),
          ...(parsedStatus != null ? { status: parsedStatus } : {}),
          ...(dueDate !== undefined && dueDate !== null
            ? { dueDate: requireDateTime(dueDate, "task", "dueDate") }
            : {}),
        });
        return out;
      },
    extend: extendOf<TaskCellValue>("task"),
  },
  weather: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [temperature, observedAt, precipitation] = args;
        const out: Weather = {
          temperature: requireNumber(temperature, "weather", "temperature"),
          observedAt: requireDateTime(observedAt, "weather", "observedAt"),
          precipitation: precipitation !== undefined && precipitation !== null ? Number(precipitation) : null,
        };
        return out;
      },
    extend: extendOf<Weather>("weather"),
  },
  slot: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [id, start, end, bookedBy] = args;
        const out: Slot = {
          id: requireId(id, "slot", "id", SlotId.parse),
          start: requireDateTime(start, "slot", "start"),
          end: requireDateTime(end, "slot", "end"),
          bookedBy: bookedBy !== undefined && bookedBy !== null ? UserId.parse(String(bookedBy)) : null,
        };
        return out;
      },
    extend: extendOf<Slot>("slot"),
  },
  report: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [title, body, producedAt, model] = args;
        const out: Report = {
          title: requireString(title, "report", "title"),
          body: requireString(body, "report", "body"),
          producedAt: requireDateTime(producedAt, "report", "producedAt"),
        };
        if (model !== undefined && model !== null) {
          out.model = String(model);
        }
        return out;
      },
    extend: extendOf<Report>("report"),
  },
  "tz-clock": {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [tz, offset, label] = args;
        const out: TzClock = { tz: requireString(tz, "tz-clock", "tz") };
        if (offset !== undefined && offset !== null) {
          out.offset = Number(offset);
        }
        if (label !== undefined && label !== null) {
          out.label = String(label);
        }
        return out;
      },
    extend: extendOf<TzClock>("tz-clock"),
  },
  sticker: {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [icon, color, tooltip] = args;
        const out: Sticker = {
          icon: requireString(icon, "sticker", "icon"),
        };
        if (color !== undefined && color !== null) {
          out.color = String(color);
        }
        if (tooltip !== undefined && tooltip !== null) {
          out.tooltip = String(tooltip);
        }
        return out;
      },
    extend: extendOf<Sticker>("sticker"),
  },
  "log-entry": {
    _: (_context: ModelLispContext) =>
      (args: Lisp.Atom[]): Lisp.Atom => {
        const [at, level, message] = args;
        const levelStr = requireString(level, "log-entry", "level");
        if (levelStr !== "info" && levelStr !== "warn" && levelStr !== "error") {
          throw new Error(
            `log-entry: level must be "info" | "warn" | "error"; got "${levelStr}"`,
          );
        }
        const out: LogEntry = {
          at: requireDateTime(at, "log-entry", "at"),
          level: levelStr,
          message: requireString(message, "log-entry", "message"),
        };
        return out;
      },
    extend: extendOf<LogEntry>("log-entry"),
  },
  date: {
    _: (context: ModelLispContext) => {
      const date = (context.tz ?? TimezoneRegion.UTC).date(
        context.column.start,
      );
      return date.toString();
    },
    dow: {
      _: (context: ModelLispContext) => {
        const date = (context.tz ?? TimezoneRegion.UTC).date(
          context.column.start,
        );
        return date.nd.dayOfWeek.shortname;
      },
      0: (context: ModelLispContext) => {
        const date = (context.tz ?? TimezoneRegion.UTC).date(
          context.column.start,
        );
        return date.nd.dayOfWeek.isoDow;
      },
      range0: (context: ModelLispContext) => {
        return context.column
          .toRange()
          .iter()
          .map((d) => d.dayOfWeek.isoDow);
      },
      range: (context: ModelLispContext) => {
        return context.column
          .toRange()
          .iter()
          .map((d) => d.dayOfWeek.shortname);
      },
      end: (context: ModelLispContext) => {
        const endDate = context.column.toRange().end.addDays(-1);
        return endDate.dayOfWeek.isoDow;
      },
    },
    dom: {
      _: (context: ModelLispContext) => {
        const date = (context.tz ?? TimezoneRegion.UTC).date(
          context.column.start,
        );
        return date.nd.dayOfMonth;
      },
      end: (context: ModelLispContext) => {
        const endDate = context.column.toRange().end.addDays(-1);
        const date = (context.tz ?? TimezoneRegion.UTC).date(endDate);
        return date.nd.dayOfMonth;
      },
      range: (context: ModelLispContext) => {
        return context.column
          .toRange()
          .iter()
          .map((d) => d.dayOfMonth);
      },
    },
    wse: {
      _: (context: ModelLispContext) => {
        const date = (context.tz ?? TimezoneRegion.UTC).date(
          context.column.start,
        );
        return date.nd.wse;
      },
      range: (context: ModelLispContext) => {
        return context.column
          .toRange()
          .iter()
          .map((d: NaiveDate) => d.wse);
      },
    },
    dse: (context: ModelLispContext) => {
      const date = (context.tz ?? TimezoneRegion.UTC).date(
        context.column.start,
      );
      return date.nd.dse;
    },
    doy: {
      _: (context: ModelLispContext) => {
        const date = (context.tz ?? TimezoneRegion.UTC).date(
          context.column.start,
        );
        return date.nd.dayOfYear;
      },
      end: (context: ModelLispContext) => {
        const endDate = context.column.toRange().end.addDays(-1);
        const date = (context.tz ?? TimezoneRegion.UTC).date(endDate);
        return date.nd.dayOfYear;
      },
    },
    weekno: (context: ModelLispContext) => {
      const date = (context.tz ?? TimezoneRegion.UTC).date(
        context.column.start,
      );
      return date.nd.isoWno;
    },
    hoursPerDay: (context: ModelLispContext) => {
      return context.column
        .toRange()
        .iter()
        .map(
          (d: NaiveDate) =>
            (context.tz ?? TimezoneRegion.UTC).date(d).date.duration.toHrsF,
        );
    },
    year: (context: ModelLispContext) => context.column.yr,
    daysInYear: (context: ModelLispContext) => Year.length(context.column.yr),
    daysInMonth: (context: ModelLispContext) => {
      const endDate = context.column.toRange().end.addDays(-1);
      return YearMonth.daysInMonth(endDate);
    },
    tz: (context: ModelLispContext) => context.tz?.fullname ?? "no tz",
    /**
     * `(date.column)` — the `NaiveDate.Partial` for the column being
     * evaluated. Exposes what `context.column` carries so formula
     * bodies can pass it through to `date.add` / `date.utc`
     * helpers without going through a separate binding.
     */
    column: (context: ModelLispContext) => (_args: Lisp.Atom[]): Lisp.Atom =>
      context.column,
    /**
     * `(date.utc "2026-04-18T15:00:00Z")` — parse an RFC3339 /
     * ISO-8601 string into a `DateTime<FixedOffset>`. Throws on a
     * malformed string. Foundational builder for event-shaped
     * formula outputs (`formula-typed-outputs.md` §2.4).
     */
    utc: (_context: ModelLispContext) => (args: Lisp.Atom[]): Lisp.Atom => {
      const s = Lisp.Utils.toStr(args[0] ?? null);
      if (!s) throw new Error("date.utc: expected RFC3339 string");
      const result = DateTime.fromRfc3339(s);
      if (result.isErr) {
        throw new Error(`date.utc: couldn't parse "${s}"`);
      }
      return result.exp();
    },
    /**
     * `(date.at "ymd-2026-4-18")` — parse a stringified
     * `NaiveDate.Partial` into the object form. Throws on malformed
     * input.
     */
    at: (_context: ModelLispContext) => (args: Lisp.Atom[]): Lisp.Atom => {
      const s = Lisp.Utils.toStr(args[0] ?? null);
      if (!s) throw new Error("date.at: expected date string");
      const result = NaiveDate.Partial.parse(s);
      if (result.isErr) {
        throw new Error(`date.at: couldn't parse "${s}"`);
      }
      return result.exp();
    },
    /**
     * `(date.now)` — current instant as `DateTime<Utc>`. Uses
     * `Date.now()`; deterministic replay requires shimming that
     * (out of scope here).
     */
    now: (_context: ModelLispContext) => (_args: Lisp.Atom[]): Lisp.Atom => {
      return DateTime.fromMse(
        MsSinceEpoch.of(Date.now()),
        DateTime.utc,
      );
    },
    /**
     * `(date.mse (date.utc "2026-01-01T08:00:00Z"))` — the
     * milliseconds-since-epoch of a `DateTime` as a number, for use as a
     * chart X coordinate. Pairs with `date.utc` to turn a JSON timestamp
     * field into an X value in a data-series formula:
     * `(date.mse (date.utc (json-get entry "created_at")))`. Matches the
     * builtin closures' `NaiveDateTime.parse(s).mse` (RFC3339 with/without
     * a trailing `Z` resolve to the same instant).
     */
    mse: (_context: ModelLispContext) => (args: Lisp.Atom[]): Lisp.Atom => {
      const dt = args[0];
      if (!(dt instanceof DateTime)) {
        throw new Error("date.mse: expected a DateTime (e.g. from date.utc)");
      }
      return dt.mse;
    },
  },
});

function calendarSystemArg(value: Option<Lisp.Atom>): string {
  if (value == null) {
    throw new Error("calendar-system: expected system keyword");
  }
  return normalizeCalendarSystemName(String(value));
}

function calendarSystemDateArg(value: Option<Lisp.Atom>): NaiveDate {
  if (value instanceof NaiveDate.Partial) {
    return value.start;
  }
  if (typeof value === "string") {
    const partial = NaiveDate.Partial.parse(value);
    if (!partial.isErr) return partial.exp().start;
    try {
      return NaiveDate.parse(value);
    } catch {
      throw new Error(`calendar-system: couldn't parse date "${value}"`);
    }
  }
  throw new Error("calendar-system: expected date");
}
